/** Lookup → Work Order page: exactly what the dispatch card shows, read-only, no pricing. */
import { useCallback, useEffect, useState } from "react";
import { tr } from "@/i18n";
import { MapPin, Building2, Wrench, Check, FileText, StickyNote, PenTool, Phone, User, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { lookupWorkOrder, getWOTechDocUrl, getPartDocUrl } from "@/lib/api";
import { isPartChecked } from "@/lib/cardProgress";
import { abbreviateName, formatNoteDate } from "@/lib/noteFormat";
import { AccessNotesPanel } from "@/components/tech/AccessNotes";
import { Section, Empty, StatusPill, InspectionRow, VisitRow, fmtDate } from "@/components/tech/lookup/LookupParts";

export default function LookupWorkOrder({ woId, openClient, openSite, openInspection, openMaps }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await lookupWorkOrder(woId); setData(r.data); }
    catch { toast.error("Could not load work order"); }
  }, [woId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-700" /></div>;

  const { work_order: wo, client, site, contact, parts, co_parts, dispatch_card_progress, field_notes, dispatch_notes, visits, inspections, wo_docs, part_docs, access_notes, equipment } = data;
  const targets = [...(site ? [{ target: "site", target_id: site.id, label: `Site: ${site.name}` }] : []), ...(client ? [{ target: "client", target_id: client.id, label: `Customer: ${client.name}` }] : [])];
  const docs = [...wo_docs.map(d => ({ ...d, url: getWOTechDocUrl(wo.id, d.id) })), ...part_docs.map(d => ({ ...d, url: getPartDocUrl(d.part_id, d.id) }))];

  return (
    <div className="space-y-2" data-testid="lookup-wo-page">
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex items-start gap-2">
          <span className="text-xs font-bold text-blue-600 shrink-0 mt-0.5">{wo.wo_number}</span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-slate-900 leading-tight break-words" data-testid="lookup-wo-title">{wo.title || "Untitled"}</h2>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <StatusPill status={wo.status} testId="lookup-wo-status" />
              {wo.scheduled_date && <span className="text-[10px] text-slate-500">Scheduled {fmtDate(wo.scheduled_date)}</span>}
              {wo.customer_po_number && <span className="text-[10px] text-slate-500">PO {wo.customer_po_number}</span>}
              {wo.dot_inspection_required && <span className="text-[9px] font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1 inline-flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />DOT</span>}
            </div>
          </div>
        </div>
        <div className="mt-2 space-y-0.5 text-[11px]">
          {client && <button type="button" onClick={() => openClient(client.id)} className="inline-flex items-center gap-1 text-slate-700 hover:text-blue-700" data-testid="lookup-wo-client-link"><Building2 className="w-3 h-3 text-slate-400" />{client.name}</button>}
          {site && (
            <div className="flex flex-wrap items-center gap-x-2">
              <button type="button" onClick={() => openSite(site.id)} className="inline-flex items-center gap-1 text-slate-700 hover:text-blue-700" data-testid="lookup-wo-site-link"><MapPin className="w-3 h-3 text-slate-400" />{site.name}</button>
              {site.address_full && <button type="button" onClick={() => openMaps(site.address_full)} className="text-blue-600 hover:underline text-left">{site.address_full}</button>}
            </div>
          )}
          {(contact?.name || contact?.phone) && (
            <div className="flex flex-wrap items-center gap-x-2 text-slate-700">
              <span className="inline-flex items-center gap-1"><User className="w-3 h-3 text-slate-400" />{contact.name || "Site contact"}</span>
              {contact.phone && <a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline inline-flex items-center gap-1"><Phone className="w-3 h-3" />{contact.phone}</a>}
            </div>
          )}
          {(wo.lift_required || wo.lift_note) && <div className="text-[10px] text-amber-800">{wo.lift_note || "Lift Required"}</div>}
        </div>
        {wo.description && <div className="mt-2 text-[11px] text-slate-600 whitespace-pre-wrap border-t border-slate-100 pt-2">{wo.description}</div>}
      </div>

      {access_notes.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
          <AccessNotesPanel notes={access_notes} targets={targets} onChanged={load} compact />
        </div>
      )}

      <Section title={tr("Tasks")} count={parts.length + co_parts.length} testId="lookup-wo-tasks">
        {parts.length + co_parts.length === 0 && <Empty text="No task list on this work order." />}
        {parts.map((p, i) => { const done = isPartChecked(p, dispatch_card_progress); return (
          <div key={`p-${i}`} className={`flex items-center gap-1 text-[11px] font-semibold px-1 py-0.5 ${done ? "text-emerald-800 bg-emerald-50 rounded" : "text-slate-800"}`}>
            {done ? <Check className="w-3 h-3 text-emerald-600" /> : <Wrench className="w-3 h-3 text-slate-500" />}{Math.round(p.quantity || 1)}x {p.description}
          </div>); })}
        {co_parts.map((p, i) => { const done = isPartChecked(p, dispatch_card_progress); return (
          <div key={`c-${i}`} className={`flex items-center gap-1 text-[11px] font-semibold px-1 py-0.5 ${done ? "text-emerald-800 bg-emerald-50 rounded" : "text-slate-800"}`}>
            {done ? <Check className="w-3 h-3 text-emerald-600" /> : <Wrench className="w-3 h-3 text-purple-500" />}{Math.round(p.quantity || 1)}x {p.description}<span className="text-[9px] text-purple-600">({p.co_number})</span>
          </div>); })}
      </Section>

      <Section title={tr("Field notes")} count={field_notes.length} testId="lookup-wo-field-notes">
        {field_notes.length === 0 ? <Empty text="No field notes yet." /> : field_notes.map((n, i) => (
          <div key={n.id || i} className="px-1 py-1 border-b border-slate-100 last:border-b-0">
            <div className="text-[11px] text-slate-800 flex items-start gap-1"><PenTool className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" /><span className="whitespace-pre-wrap break-words">{n.text}</span></div>
            <div className="text-[9px] text-slate-400 pl-4">{abbreviateName(n.tech_name || n.created_by)} · {formatNoteDate(n.created_at)}</div>
          </div>
        ))}
      </Section>

      {dispatch_notes.length > 0 && (
        <Section title={tr("Dispatch notes")} count={dispatch_notes.length} testId="lookup-wo-dispatch-notes">
          {dispatch_notes.map((n, i) => (
            <div key={n.id || i} className="px-1 py-1 border-b border-slate-100 last:border-b-0 text-[11px] text-slate-700 flex items-start gap-1">
              <StickyNote className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" /><span className="whitespace-pre-wrap break-words">{n.text || n.note}</span>
              <span className="text-[9px] text-slate-400 ml-auto shrink-0">{n.date}</span>
            </div>
          ))}
        </Section>
      )}

      <Section title={tr("Visits")} count={visits.length} testId="lookup-wo-visits">
        {visits.length === 0 ? <Empty text="Not dispatched yet." /> : visits.map(v => <VisitRow key={v.id} v={v} />)}
      </Section>

      <Section title={tr("Inspections")} count={inspections.length} testId="lookup-wo-inspections">
        {inspections.length === 0 ? <Empty text="No inspections on this work order." /> : inspections.map(i => <InspectionRow key={i.id} insp={i} onOpen={openInspection} />)}
      </Section>

      <Section title={tr("Tech docs")} count={docs.length} testId="lookup-wo-docs">
        {docs.length === 0 ? <Empty text="No documents attached." /> : docs.map(d => (
          <a key={d.id} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-1 py-1 text-[11px] text-blue-700 hover:underline border-b border-slate-100 last:border-b-0" data-testid={`lookup-doc-${d.id}`}>
            <FileText className="w-3 h-3 shrink-0" /><span className="truncate">{d.filename}</span>{d.part_name && <span className="text-slate-400 truncate">· {d.part_name}</span>}
          </a>
        ))}
      </Section>

      {equipment.length > 0 && (
        <Section title={tr("Equipment")} count={equipment.length} testId="lookup-wo-equipment">
          <div className="flex flex-wrap gap-1">{equipment.map(e => <span key={e} className="text-[10px] rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-slate-700">{e}</span>)}</div>
        </Section>
      )}
    </div>
  );
}
